import React from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    VIEW, EDIT, OPEN,
    ACHIEVED, INCREASE_TO, DECREASE_TO, STAY_ABOVE, STAY_BELOW,
} from "const"
import { okrKeyResultsRoute } from "client/parts/routes"
import { hasAccess } from "client/utils"
import {
    PRM_OKR_OBJECTIVE_KEY_RESULT,
    StoreType,
    OkrKeyResultObjectProps,
    IsOwnerOkrObjective,
    IsOwnerOkrKeyResult,
    addModal,
} from "client/store"
import {
    FormGroup,
    Label,
    DivInput,
    Link,
    Input,
    Textarea,
    UserIcon,
    TagEditor,
    Button,
} from "client/components"
import {
    KeyResultForm,
    OwnerList,
} from "client/pages/okr/styled"
import {
    MetricInput,
    TargetDescription,
} from "../styled"

const Form = ({
    objective,
    keyResult,
}: OkrKeyResultObjectProps) => {
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)
    const objectiveBaseRoute = useSelector((state: StoreType) => state.okrObjective.baseRoute)
    const objectiveRoute = useSelector((state: StoreType) => state.okrObjective.objectiveRoute)
    const metricList = useSelector((state: StoreType) => state.okrMetric.metricList)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const isOwnerKeyResult = IsOwnerOkrKeyResult(keyResult, userData.userId)
    const isOwnerObjective = IsOwnerOkrObjective(objective, userData.userId)
    const isOwnerSession = session.owner === userData.userId
    const isOwner = isOwnerKeyResult || isOwnerObjective || isOwnerSession

    const isOpenSession = session.status === OPEN

    const editAccess = hasAccess(PRM_OKR_OBJECTIVE_KEY_RESULT, EDIT)
    const allowedToEdit = (editAccess || isOwner) && isOpenSession

    const objectiveParentUrl = `${objectiveBaseRoute}${objectiveRoute}/${VIEW}/${objective.id}`
    const editUrl = `${objectiveParentUrl}${okrKeyResultsRoute}/${EDIT}/${keyResult.id}`

    const metric = metricList[keyResult.metricId] || null

    let targetDescription: string
    if (keyResult.type !== ACHIEVED) {
        const metricBefore = metric && !metric.displayAfter ? metric.symbol : ""
        const metricAfter = metric && metric.displayAfter ? metric.symbol : ""

        const target = `${metricBefore} ${keyResult.targetNumber} ${metricAfter}`
        const critical = keyResult.useCriticalTarget && keyResult.criticalNumber !== null ? keyResult.criticalNumber : null
        const criticalValue = critical !== null
            ? `${metricBefore} ${keyResult.criticalNumber} ${metricAfter}`
            : ""

        let descriptionStart: string
        let descriptionEnd = ""
        if (keyResult.type === INCREASE_TO) descriptionStart = lang.shouldIncreaseTo
        if (keyResult.type === DECREASE_TO) descriptionStart = lang.shouldDecreaseTo
        if (keyResult.type === STAY_ABOVE) {
            descriptionStart = lang.shouldStayAbove
            descriptionEnd = lang.mustBeAbove
        }
        if (keyResult.type === STAY_BELOW) {
            descriptionStart = lang.shouldStayBelow
            descriptionEnd = lang.mustBeBelow
        }
        targetDescription = `${descriptionStart} ${target} ${critical !== null ? `${descriptionEnd} ${criticalValue}` : ""}`
    }

    const selectedTags: number[] = []
    if (keyResult && keyResult.tags) {
        keyResult.tags.forEach((row) => {
            selectedTags.push(row.tagId)
        })
    }

    return (
        <KeyResultForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.parentObjective}</Label>
                        <DivInput widePadding>
                            <Link
                                href={objectiveParentUrl}
                                onClick={() => dispatch(addModal({ url: objectiveParentUrl }))}
                            >
                                {objective.title}
                            </Link>
                        </DivInput>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={keyResult.description} readOnly />
                    </FormGroup>

                    {
                        keyResult.type !== ACHIEVED && (
                            <>
                                <FormGroup key="initial">
                                    <Label>{lang.initial}</Label>
                                    <MetricInput
                                        metric={metric ? metric.symbol : null}
                                        metricAfter={metric ? metric.displayAfter : null}
                                        value={keyResult.initialNumber !== null ? keyResult.initialNumber : ""}
                                        readOnly
                                    />
                                </FormGroup>
                                <FormGroup key="actual">
                                    <Label>{lang.actual}</Label>
                                    <MetricInput
                                        metric={metric ? metric.symbol : null}
                                        metricAfter={metric ? metric.displayAfter : null}
                                        value={keyResult.actualNumber !== null ? keyResult.actualNumber : ""}
                                        readOnly
                                    />
                                </FormGroup>
                                <FormGroup key="target">
                                    <Label>{lang.target}</Label>
                                    <MetricInput
                                        metric={metric ? metric.symbol : null}
                                        metricAfter={metric ? metric.displayAfter : null}
                                        value={keyResult.targetNumber !== null ? keyResult.targetNumber : ""}
                                        readOnly
                                    />
                                    <TargetDescription style={{ marginBottom: "5px" }}>{targetDescription}</TargetDescription>
                                </FormGroup>
                            </>
                        )
                    }
                    {
                        keyResult.type === ACHIEVED && (
                            <FormGroup>
                                <Label>{lang.status}</Label>
                                <Input
                                    type="text"
                                    value={keyResult.actualNumber === keyResult.targetNumber ? lang.achieved : lang.notAchieved}
                                    readOnly
                                />
                            </FormGroup>
                        )
                    }

                    <FormGroup>
                        <Label>{lang.owners}</Label>
                        <OwnerList>
                            {
                                keyResult.owners && keyResult.owners.map((row) => (
                                    <UserIcon key={row.userId} user={userList[row.userId]} size="sm" />
                                ))
                            }
                        </OwnerList>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.tags}</Label>
                        <TagEditor
                            selectedIds={selectedTags}
                            readOnly
                        />
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    {
                        allowedToEdit && (
                            <Button onClick={() => dispatch(addModal({ url: editUrl }))}>
                                {lang.edit}
                            </Button>
                        )
                    }
                </Col>
            </Row>
        </KeyResultForm>
    )
}
export default Form
